import React, { useCallback, useEffect } from "react";
import { useDispatch, useSelector, RootStateOrAny } from "react-redux";
import { useHistory } from "react-router-dom";
import * as XLSX from "xlsx";

import {
  ListClientsActions,
  ListClientsState,
  PaginateClientsState,
} from "store/ducks/settings/clients";

import * as S from "./styles";
import { MainContainer, Paginator, MenuSelector } from "components/shared";
import { GridClients, FilterClients } from "components/settings";
import { useTranslation, usePermissions } from "hooks";
import { translations } from "./translations";
import { useFilters } from 'hooks/useFilters';

export const ListClients: React.FC = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { getTranslation } = useTranslation(translations);
  const { checkUserPermission } = usePermissions();

  const { loading: loadingExport } = useSelector<RootStateOrAny>(
    (state) => state.listClients
  ) as ListClientsState;

  const { data, loading, pagination } = useSelector<RootStateOrAny>(
    (state) => state.paginateClients
  ) as PaginateClientsState;

  const { getData, filters } = useFilters('clients')

  const exportToXLSX = useCallback((dados: any) => {
    if (dados.length > 0) {
      const nameFile = "RELATÓRIO-CLIENTES.xlsx";
      const file = XLSX.utils.book_new();
      const wsData = dados.map((clients: any) => [
        clients.id || "---",
        clients?.cnpj || "---",
        clients.company_name || "---",
        clients.trade_name || "---",
        clients.inscricao_estadual || "---",
        clients.phone_number || "---",
        clients.email || "---",
        clients.address_zipcode || "---",
        clients.address_street || "---",
        clients.address_number || "---",
        clients.address_neighborhood || "---",
        clients.address_state || "---",
        clients.address_city || "---",
        (Array.isArray(clients.clientCodes)
          ? clients.clientCodes.map((c: any) => c.client_code).join(", ")
          : "") || "---",
        clients.general_client_type_id === 1
          ? "STAR"
          : clients.general_client_type_id === 2
          ? "PLATINUM"
          : clients.general_client_type_id === 3
          ? "GOLD"
          : clients.general_client_type_id === 4
          ? "SILVER"
          : "---",
        clients?.clientCodes?.[0]?.latitude ?? "---",
        clients?.clientCodes?.[0]?.longitude ?? "---",
        clients.general_country_id === 32
          ? "BR"
          : clients.general_country_id === 52
          ? "CO"
          : clients.general_country_id === 12
          ? "AR"
          : clients.general_country_id === 174
          ? "PE"
          : "EX",
        clients.observation || "---",
      ]);

      const worksheet = XLSX.utils.aoa_to_sheet([
        [
          getTranslation("id"),
          getTranslation("cnpjCpf"),
          getTranslation("razaoSocial"),
          getTranslation("trade_name"),
          getTranslation("inscricao_estadual"),
          getTranslation("phone_number"),
          getTranslation("email"),
          getTranslation("address_zipcode"),
          getTranslation("address_street"),
          getTranslation("address_number"),
          getTranslation("address_neighborhood"),
          getTranslation("address_state"),
          getTranslation("address_city"),
          getTranslation("codigoSap"),
          getTranslation("customerSegment"),
          getTranslation("latitude"),
          getTranslation("longitude"),
          getTranslation("pais"),
          getTranslation("observation"),
        ],
        ...wsData,
      ]);
      XLSX.utils.book_append_sheet(file, worksheet, "Dados");
      XLSX.writeFile(file, nameFile);
    } else {
      alert("Não há dados para exportar");
    }
  }, [getTranslation]);

  const getListClients = useCallback(() => {
    dispatch(
      ListClientsActions.request({ ...filters, all: true }, (dados: any) => {
        exportToXLSX(dados);
      })
    );
  }, [dispatch, filters, exportToXLSX]);

  useEffect(() => {
    getData();
  }, [getData]);

  return (
    <MainContainer>
      <S.PageHeader>
        <h1>
          <S.IconSetting />
          {getTranslation("configuracoes")} <MenuSelector page="clients" />
          {loading && <S.LoadingPage />}
        </h1>
        <S.HeaderButtons>
          {checkUserPermission("insertClient") && (
            <S.ButtonMini onClick={getListClients}>
              {loadingExport ? <S.LoadingPage /> : getTranslation("exportar")}
            </S.ButtonMini>
          )}
          {checkUserPermission("insertClient") && (
            <S.ButtonMini onClick={() => history.push("/settings/client/new")}>
              {getTranslation("novoCliente")}
            </S.ButtonMini>
          )}
          <FilterClients />
        </S.HeaderButtons>
      </S.PageHeader>
      <S.PageContent>
        <GridClients clients={data} />
        <Paginator
          pagination={pagination}
          settingsPage='clients'
          loading={loading}
        />
      </S.PageContent>
    </MainContainer>
  );
};
